import { SpinLoading } from "antd-mobile";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { CustomDialog } from "../../../components";
import CustomPopup from "../../../components/CustomPopup";

import { useAuth } from "../../../context/AuthContext";
import localStorageKey from "../../../enumerations/localStorageKey";
import { moneyFormat } from "../../../plugins/numberFormat";
import { envChangeType } from "../../../plugins/utils";
import { ApplicationForm } from "./components";
import agentStatus from "./enumerations/agentStatus";
import applicationRequiredStatus from "./enumerations/applicationRequiredStatus";
import { useApplyAgent, useGetAgentStatus } from "./hooks/apply.hooks";
import styles from "./style.module.scss";

const Apply = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { setToken, setLang, lang, setDefaultCurrency, queryCurrency, queryLang, queryToken } =
    useAuth();
  const { data, isLoading, isError, error } = useGetAgentStatus({
    options: {
      enabled: !!queryToken === false,
    },
  });
  const { mutate: applyAgent } = useApplyAgent();
  const [agentApplySetting, setAgentApplySetting] = useState(null);
  const [depositQualified, setDepositQualified] = useState(false);
  const [betQualified, setBetQualified] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [requireValidation, setRequireValidation] = useState(applicationRequiredStatus.REQUIRED);
  const [currentApplicationStatus, setCurrentApplicationStatus] = useState(null);

  useEffect(() => {
    if (data) {
      localStorage.setItem(localStorageKey.USER_CURRENCY, data.currency);
      switch (data.agent_apply_status) {
        case agentStatus.APPROVED:
          // navigate("/home", { replace: true });
          window.location.href = "/home";
          break;
        case agentStatus.PENDING:
          setCurrentApplicationStatus(agentStatus.PENDING);
          setDepositQualified(true);
          setBetQualified(true);
          break;
        case agentStatus.REJECTED:
          navigate(`/apply/rejected?currency=${data.currency}`, {
            replace: true,
          });
          break;
        default:
          setAgentApplySetting({
            ...data.agent_apply_setting,
            total_bet_amount: data.total_bet_amount,
            total_deposit_amount: data.total_deposit_amount,
          });
          setDepositQualified(
            data.total_deposit_amount >= data.agent_apply_setting.agent_apply_req_total_deposit ||
              data.agent_apply_status === agentStatus.PENDING,
          );
          setBetQualified(
            data.total_bet_amount >= data.agent_apply_setting.agent_apply_req_total_bet ||
              data.agent_apply_status === agentStatus.PENDING,
          );
          setRequireValidation(parseInt(data.agent_apply_setting.agent_apply_req_verification));
          break;
      }
    }
  }, [data]);

  useEffect(() => {
    if (queryToken !== null) {
      const isTokenString = typeof queryToken === "string";
      const _token = isTokenString ? queryToken.replace(/["]+/g, "") : "";
      const _lang = queryLang
        ? queryLang.replace(/["]+/g, "")
        : envChangeType("REACT_APP_DEFAULT_LANG");
      const _currency = queryCurrency
        ? queryCurrency.replace(/["]+/g, "").toUpperCase()
        : envChangeType("REACT_APP_DEFAULT_CURRENCY");
      setToken(_token);
      setLang(_lang);
      setDefaultCurrency(_currency);
      i18n.changeLanguage(_lang);
      window.location.href = `${window.location.origin}`;
    }
  }, []);

  const onHandleOpenApplicationForm = () => {
    switch (currentApplicationStatus) {
      case agentStatus.PENDING:
        CustomDialog({
          type: "show",
          dialogProps: {
            header: t("apply.pending"),
            destroyOnClose: true,
            closeOnAction: true,
            content: t("apply.pending.subtitle"),
            actions: [
              {
                key: "close",
                text: t("mobile.normal.understand"),
              },
            ],
          },
        });

        break;
      default:
        if (betQualified === false || depositQualified === false) return;

        //CHANGES 2023/02/07: validation not required = immediately submit application, doesn't need to open form
        switch (requireValidation) {
          case applicationRequiredStatus.OPTIONAL:
            onHandleSubmitForm();
            break;
          case applicationRequiredStatus.REQUIRED:
            setShowForm(true);
            break;
          default:
            break;
        }
    }
  };

  const onHandleSubmitForm = async () => {
    await applyAgent(
      {
        params: {},
      },
      {
        onSuccess: (data) => {
          CustomDialog({
            type: "show",
            dialogProps: {
              header: t("apply.success"),
              destroyOnClose: true,
              closeOnAction: true,
              content: t("apply.success.subtitle"),
              actions: [
                {
                  key: "close",
                  text: t("mobile.normal.understand"),
                },
              ],
              onAction: (action, index) => {
                if (action.key === "close") {
                  setCurrentApplicationStatus(agentStatus.PENDING);
                }
              },
            },
          });
        },
        // onError: (error) => {
        //   Toast.show({ icon: "fail", content: error });
        // },
      },
    );
  };

  const renderStatus = (qualified) => {
    if (qualified === undefined || qualified === null) return;
    if (qualified === true) {
      return t("mobile.apply.requirementStatus.done");
    }
    return t("mobile.apply.requirementStatus.notDone");
  };

  const renderContent = () => {
    if (isError) return;
    if (isLoading || (!isLoading && data && data.agent_apply_status === agentStatus.APPROVED)) {
      return <SpinLoading className={styles.loading} />;
    }
    return (
      <div className={styles.content}>
        <div className={styles.term}>{t("apply.termLiine")}</div>
        <div className={styles.cardContainer}>
          <div className={styles.titleContainer}>
            <div className={styles.title}>
              {t("mobile.apply.requirement1")}
              <span className={styles.requirementTitle}> {t("mobile.apply.totalDeposit")}</span>
              {t("mobile.apply.requirement2")}
              {moneyFormat(agentApplySetting?.agent_apply_req_total_deposit || 0)}
            </div>
            <div
              className={`${styles.currentAmount} ${
                depositQualified === false ? styles.failed : ""
              }`}
            >
              {t("mobile.apply.currentAmount", {
                amount: moneyFormat(agentApplySetting?.total_deposit_amount || 0),
              })}
            </div>
          </div>
          <div className={`${styles.status} ${depositQualified === false ? styles.failed : ""}`}>
            {renderStatus(depositQualified)}
          </div>
        </div>
        <div className={styles.cardContainer}>
          <div className={styles.titleContainer}>
            <div className={styles.title}>
              {t("mobile.apply.requirement1")}
              <span className={styles.requirementTitle}> {t("mobile.apply.totalBetAmount")}</span>
              {t("mobile.apply.requirement2")}
              {` ${moneyFormat(agentApplySetting?.agent_apply_req_total_bet || 0)}`}
            </div>
            <div
              className={`${styles.currentAmount} ${betQualified === false ? styles.failed : ""}`}
            >
              {t("mobile.apply.currentAmount", {
                amount: moneyFormat(agentApplySetting?.total_bet_amount || 0),
              })}
            </div>
          </div>
          <div className={`${styles.status} ${betQualified === false ? styles.failed : ""}`}>
            {renderStatus(betQualified)}
          </div>
        </div>
      </div>
    );
  };

  const renderButton = () => {
    if (
      isLoading ||
      (!isLoading && data && data.agent_apply_status === agentStatus.APPROVED) ||
      isError
    )
      return;
    return (
      <div
        onClick={onHandleOpenApplicationForm}
        className={`${styles.applyButton} ${
          betQualified === false || depositQualified === false ? styles.inactive : ""
        }`}
      >
        {t("personal.agentList.apply")}
      </div>
    );
  };

  const renderBackground = () => {
    const _lang = lang || envChangeType("REACT_APP_DEFAULT_LANG");
    return {
      backgroundImage: `url('images/apply/${_lang}/background.jpg')`,
      backgroundRepeat: "no-repeat",
      backgroundPosition: "top",
      backgroundSize: "cover",
    };
  };

  return (
    <div className={`${styles.container}`} style={renderBackground()}>
      {renderContent()}
      {renderButton()}
      <CustomPopup
        visible={showForm}
        onClose={() => setShowForm(false)}
        title={t("mobile.apply.formTitle")}
        destroyOnClose={true}
      >
        <ApplicationForm
          requireValidation={requireValidation}
          setShowForm={setShowForm}
          setCurrentApplicationStatus={setCurrentApplicationStatus}
        />
      </CustomPopup>
    </div>
  );
};

export default Apply;
